import { useRef, useState, useCallback } from 'react';
import { KonvaEventObject } from 'konva/lib/Node';

interface UseLongPressOptions<T> {
	delay: number;
	enabled: boolean; 
	onLongPress: (position: { x: number; y: number }, pointIndex: number, segmentId: string) => void;
}

export const useLongPress = <T>({ delay = 2000, enabled = true, onLongPress }: UseLongPressOptions<T>) => {
	const longTouchTimerRef = useRef<NodeJS.Timeout | null>(null);
	const startPointRef = useRef<{ x: number; y: number } | null>(null);
	const longPressTriggeredRef = useRef(false);

	const handleTouchStart = useCallback(
		(e: KonvaEventObject<TouchEvent>, customData?: any) => {
			if (!enabled) return;

			const touch = e.evt.touches[0];
			startPointRef.current = { x: touch.clientX, y: touch.clientY };

			longPressTriggeredRef.current = false;
			clearTimeout(longTouchTimerRef.current!);
			longTouchTimerRef.current = setTimeout(() => {
				longPressTriggeredRef.current = true;
				if (onLongPress) {
					onLongPress(
						{ x: touch.clientX, y: touch.clientY },
						customData?.pointIndex,
						customData?.segmentId
					);
				}
			}, delay);
		},
		[enabled, delay, onLongPress]
	);

	const handleTouchEndOrCancel = useCallback(() => {
		clearTimeout(longTouchTimerRef.current!);
	}, []);

	const handleTouchMove = useCallback((e: KonvaEventObject<TouchEvent>) => {
		if (!startPointRef.current) return;

		const touch = e.evt.touches[0];
		const dx = touch.clientX - startPointRef.current.x;
		const dy = touch.clientY - startPointRef.current.y;
		const dist = Math.hypot(dx, dy);

		// Cancel long press if finger moved too far OR multi-touch
		if (dist > 5 || e.evt.touches.length > 1) {
			clearTimeout(longTouchTimerRef.current!);
		}
	}, []);

	return {
		handleTouchStart,
		handleTouchEndOrCancel,
		handleTouchMove,
		longTouchTimerRef,
		longPressTriggeredRef,
	};
};
